import React, { useState, useEffect } from 'react';
import { Property } from '../types/property.types';
import { propertyService } from '../services/propertyService';

interface SmartPropertyCodesProps {
  property: Property;
  compact?: boolean;
  autoDiscover?: boolean;
}

interface PlatformUrlResult {
  success: boolean;
  platform_urls: Record<string, string>;
  summary: {
    total_platforms: number;
    found_count: number;
    success_rate: string;
  };
}

const SmartPropertyCodes: React.FC<SmartPropertyCodesProps> = ({ 
  property, 
  compact = false,
  autoDiscover = true
}) => {
  const [platformUrls, setPlatformUrls] = useState<PlatformUrlResult | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // Auto-descubrir URLs al cargar
  useEffect(() => {
    if (autoDiscover && (property.codigo_inmueble || property.id)) {
      discoverUrls();
    }
  }, [property.codigo_inmueble, property.id, autoDiscover]);

  const discoverUrls = async () => {
    if (!property.codigo_inmueble && !property.id) return;
    
    setIsLoading(true);
    try {
      const result = await propertyService.getPlatformUrls(property.codigo_inmueble || property.id);
      setPlatformUrls(result);
    } catch (error) {
      console.error('Error discovering URLs:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Configuración de plataformas
  const platformConfigs = [
    {
      key: 'prointegral',
      platform: 'ProIntegral',
      code: property.prointegral,
      icon: '🏢',
      color: 'bg-blue-100 text-blue-800 hover:bg-blue-200',
      staticUrl: property.prointegral ? `https://www.prointegral.com.co/inmueble/${property.prointegral}` : null
    },
    {
      key: 'fincarraiz',
      platform: 'FincaRaíz',
      code: property.finca_raiz,
      icon: '🏠',
      color: 'bg-green-100 text-green-800 hover:bg-green-200',
      staticUrl: property.finca_raiz ? `https://www.fincaraiz.com.co/inmueble/${property.finca_raiz}` : null
    },
    {
      key: 'metrocuadrado',
      platform: 'MetroCuadrado',
      code: property.metro_cuadrado,
      icon: '📏',
      color: 'bg-purple-100 text-purple-800 hover:bg-purple-200',
      staticUrl: property.metro_cuadrado ? `https://www.metrocuadrado.com/detalle/${property.metro_cuadrado}` : null
    },
    {
      key: 'ciencuadras',
      platform: 'CienCuadras',
      code: property.cien_cuadras,
      icon: '💯',
      color: 'bg-orange-100 text-orange-800 hover:bg-orange-200',
      staticUrl: property.cien_cuadras ? `https://www.ciencuadras.com/inmueble/codigo-${property.cien_cuadras}` : null
    },
    {
      key: 'proppit',
      platform: 'PropPit',
      code: property.proppit,
      icon: '🎯',
      color: 'bg-red-100 text-red-800 hover:bg-red-200',
      staticUrl: property.proppit ? `https://www.properati.com.co/detalle/${property.proppit}` : null
    },
    {
      key: 'espaciourbano',
      platform: 'EspacioUrbano',
      code: property.espacio_urbano,
      icon: '🌆',
      color: 'bg-indigo-100 text-indigo-800 hover:bg-indigo-200',
      staticUrl: property.espacio_urbano ? `https://www.espaciourbano.com/Ficha.asp?xId=${property.espacio_urbano}` : null
    }
  ].filter(item => item.code && item.code.trim() !== '' && item.code !== 'NO WEB');

  const renderPlatformCode = (config: typeof platformConfigs[0]) => {
    const discoveredUrl = platformUrls?.platform_urls[config.key];
    const finalUrl = (discoveredUrl && discoveredUrl !== 'No encontrado') ? discoveredUrl : config.staticUrl;
    const hasDiscoveredUrl = discoveredUrl && discoveredUrl !== 'No encontrado';
    
    if (!finalUrl) {
      return (
        <div className={`flex items-center space-x-2 px-3 py-1.5 rounded-full text-sm font-medium ${config.color} opacity-60`}>
          <span>{config.icon}</span>
          <span>{config.code}</span>
          <span className="text-xs">❌</span>
        </div>
      );
    }

    return (
      <a
        href={finalUrl}
        target="_blank"
        rel="noopener noreferrer"
        className={`flex items-center space-x-2 px-3 py-1.5 rounded-full text-sm font-medium transition-all duration-200 ${config.color} hover:shadow-md hover:scale-105 cursor-pointer`}
        title={`Ver en ${config.platform}: ${config.code}`}
        onClick={(e) => e.stopPropagation()}
      >
        <span>{config.icon}</span>
        <span className="font-mono">{config.code}</span>
        {hasDiscoveredUrl ? (
          <span className="text-green-600" title="URL encontrada con Ultra MCP">✅</span>
        ) : (
          <span className="text-gray-400" title="URL estática">🔗</span>
        )}
      </a>
    );
  };

  // Si no hay códigos
  if (platformConfigs.length === 0) {
    return (
      <div className="flex items-center space-x-2">
        <span className="text-xs text-gray-500 px-2 py-1 bg-gray-100 rounded">
          ID: {property.codigo_inmueble || property.id || 'Sin código'}
        </span>
      </div>
    );
  }

  // Vista compacta
  if (compact) {
    return (
      <div className="flex items-center space-x-2">
        {renderPlatformCode(platformConfigs[0])}
        {isLoading && (
          <div className="w-4 h-4 border border-blue-400 border-t-transparent rounded-full animate-spin"></div>
        )}
        {platformConfigs.length > 1 && (
          <span className="text-xs text-gray-400">+{platformConfigs.length - 1}</span>
        )}
      </div>
    );
  }

  // Vista normal
  return (
    <div className="space-y-2">
      {platformConfigs.map((config) => (
        <div key={config.key}>
          {renderPlatformCode(config)}
        </div>
      ))}
      
      {autoDiscover && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            discoverUrls();
          }}
          disabled={isLoading}
          className="mt-2 px-3 py-1 text-xs bg-blue-100 text-blue-800 hover:bg-blue-200 rounded flex items-center space-x-1"
        >
          {isLoading ? (
            <>
              <div className="w-3 h-3 border border-blue-400 border-t-transparent rounded-full animate-spin"></div>
              <span>Buscando...</span>
            </>
          ) : (
            <>
              <span>🚀</span>
              <span>Actualizar enlaces</span>
            </>
          )}
        </button>
      )}
    </div>
  );
};

export default SmartPropertyCodes;
